/**
 * TopNavMenu — right-side context menu in the mobile TopNavBar.
 *
 * Things 3 pattern: context-aware menu adapts per view type.
 * - Container views (project/list/area): Add Section, Paste, Share
 * - Static views (today/inbox/etc.): New Project, Paste, Share
 *
 * Uses Radix DropdownMenu for accessible, animated overlay.
 */

import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import EllipsisVertical from "@/shared/icons/pikaicons/stroke/general/three-dots-menu-vertical.svg?react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu"
import { getViewFromPathname } from "@/features/views/viewRegistry"
import { toast } from "sonner"
import { addSection, addTask, addProject } from "@/db/operations"
import { ObjectType } from "@/db/schema"
import { readClipboard } from "@/shared/utils/platform/clipboard"
import { shareContent, getAppUrl } from "@/shared/utils/platform/share"
import { pasteTasksFromClipboard } from "@/shared/utils/task/pasteTasks"
import { useViewContext } from "@/app/stores/viewContext"
import { useViewMenuStore } from "@/features/views/stores/viewMenuStore"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { ROUTES } from "@/config/routes"
import SectionIcon from "@/shared/icons/totodoicons/stroke/section.svg?react"
import ClipboardIcon from "@/shared/icons/totodoicons/stroke/clipboard.svg?react"
import ShareIcon from "@/shared/icons/totodoicons/stroke/share.svg?react"
import PlusCircleIcon from "@/shared/icons/pikaicons/stroke/maths/plus-circle.svg?react"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Views where "New Project" makes sense */
const NEW_PROJECT_VIEWS = ["today", "someday", "anyday", "area"]

/** Views where "Paste" makes sense (can create tasks) */
const PASTE_VIEWS = [
  "today", "evening", "upcoming", "someday", "anyday", "inbox",
  "project", "list", "area",
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TopNavMenu() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const selectAll = useViewContext((s) => s.selectAll)
  const clearSelection = useViewContext((s) => s.clearSelection)
  const selectedCount = useViewContext((s) => s.selectedTaskIds.length)
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)

  // Page actions from viewMenuStore (set by ViewShell)
  const pageActions = useViewMenuStore((s) => s.pageActions)
  const totalCount = useViewMenuStore((s) => s.totalCount)
  const allSelected = totalCount > 0 && selectedCount >= totalCount

  const view = getViewFromPathname(pathname)
  const isContainerView =
    view?.id === "project" || view?.id === "list" || view?.id === "area"
  const routeContainerId = params.id

  // --- Add Section (container views only) ---
  const handleAddSection = useCallback(async () => {
    if (!routeContainerId) return
    const parentPath = `root/${routeContainerId}`
    const result = await addSection("", parentPath, {
      insertPosition: "top",
    })
    if (result.ok) {
      const id = result.data.id
      const focusEl = (attempt = 0) => {
        const el = document.querySelector(
          `[data-task-id="${id}"] [contenteditable]`,
        )
        if (el instanceof HTMLElement) {
          el.focus()
        } else if (attempt < 30) {
          requestAnimationFrame(() => focusEl(attempt + 1))
        }
      }
      requestAnimationFrame(() => focusEl())
    }
  }, [routeContainerId])

  // --- New Project ---
  const handleNewProject = useCallback(async () => {
    const result = await addProject("")
    if (!result.ok) return
    useEditFocusStore.getState().requestEditFocus(result.data.id)
    navigate(ROUTES.PROJECT(result.data.id))
  }, [navigate])

  // --- Paste from clipboard ---
  const handlePaste = useCallback(async () => {
    try {
      // Static views read the clipboard themselves (shared paste path)
      if (!(isContainerView && routeContainerId)) {
        await pasteTasksFromClipboard({ viewId, containerId, eveningHour })
        return
      }

      const text = await readClipboard()
      if (!text.trim()) {
        toast(t("Nothing to paste"))
        return
      }

      const lines = text
        .split(/\r?\n/)
        .filter((l) => l.trim().length > 0)

      // Container views: paste into the container with markdown parsing
      if (isContainerView && routeContainerId) {
        const parentPath = `root/${routeContainerId}`
        let created = 0
        let failed = 0
        for (const raw of lines) {
          let title = raw.trim()
          let type = ObjectType.Task

          if (title.startsWith("## ")) {
            type = ObjectType.Section
            title = title.slice(3).trim()
          } else if (title.startsWith("# ")) {
            type = ObjectType.Section
            title = title.slice(2).trim()
          }

          if (title.startsWith("- [ ] ")) {
            title = title.slice(6).trim()
          } else if (title.startsWith("- [x] ") || title.startsWith("- [~] ")) {
            continue
          } else if (title.startsWith("- ")) {
            title = title.slice(2).trim()
          }

          if (!title) continue

          const result =
            type === ObjectType.Section
              ? await addSection(title, parentPath)
              : await addTask({ title, parentPath })
          if (result.ok) created++
          else failed++
        }
        if (failed > 0) toast.error(t("Failed to paste tasks"))
        else if (created === 1) toast(t("Task created"))
        else if (created > 1) toast(t("{{count}} tasks created", { count: created }))
      }
    } catch (e) {
      console.error("[TopNavMenu] Paste failed:", e)
      toast.error(t("Failed to paste tasks"))
    }
  }, [isContainerView, routeContainerId, viewId, containerId, eveningHour, t])

  // --- Share ---
  const handleShare = useCallback(async () => {
    await shareContent({
      title: "ToToDo",
      url: getAppUrl(window.location.pathname + window.location.search),
    })
  }, [])

  // --- Select All / Deselect All ---
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      clearSelection()
    } else {
      selectAll()
    }
  }, [selectAll, clearSelection, allSelected])

  const showNewProject = NEW_PROJECT_VIEWS.includes(viewId)
  const showPaste = PASTE_VIEWS.includes(viewId)
  const showAddSection = isContainerView && !!routeContainerId
  const hasTopSection = showAddSection || showNewProject || totalCount > 0 || pageActions.length > 0

  // Hide the entire menu on views where no actions make sense
  if (viewId === "recurring" || viewId === "routines") {
    return <div className="size-9" aria-hidden="true" />
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          aria-label={t("More options")}
        >
          <EllipsisVertical className="size-5" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={4}>
        {/* Select All / Deselect All */}
        {totalCount > 0 && (
          <DropdownMenuItem onSelect={handleSelectAll}>
            <span>{allSelected ? t("Deselect All") : t("Select All")}</span>
          </DropdownMenuItem>
        )}

        {/* Page-specific actions (e.g. "Group by Project") */}
        {pageActions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            onSelect={action.onClick}
            disabled={action.disabled}
          >
            {action.icon && (
              <span className="mr-2 inline-flex size-4 items-center justify-center">
                {action.icon}
              </span>
            )}
            <span>{action.label}</span>
          </DropdownMenuItem>
        ))}

        {/* Add Section (container views) */}
        {showAddSection && (
          <>
            {totalCount > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem onSelect={handleAddSection}>
              <SectionIcon className="size-4" />
              <span>{t("Add Section")}</span>
            </DropdownMenuItem>
          </>
        )}

        {/* New Project (static views that support it) */}
        {showNewProject && (
          <DropdownMenuItem onSelect={handleNewProject}>
            <PlusCircleIcon className="size-4" />
            <span>{t("New Project")}</span>
          </DropdownMenuItem>
        )}

        {hasTopSection && showPaste && <DropdownMenuSeparator />}

        {/* Paste from clipboard */}
        {showPaste && (
          <DropdownMenuItem onSelect={handlePaste}>
            <ClipboardIcon className="size-4" />
            <span>{t("Paste")}</span>
          </DropdownMenuItem>
        )}

        {/* Share */}
        <DropdownMenuItem onSelect={handleShare}>
          <ShareIcon className="size-4" />
          <span>{t("Share")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
